import { styled, TextField, TextFieldProps } from '@mui/material'
import {
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  Title,
} from '.'
import {
  ChangeEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { useVersionReportVersionRepository } from '../../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { Button } from '../../../../components/buttons'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import Loading from '../../../../components/process-state-notifications/Loading'

type NewVersionDialogProps = {
  open: boolean
  templateUuid: string | undefined
  onAfterSubmit: (
    uuid: string,
    name: string,
    isInitial: boolean,
    version: VersionReportVersionEntity
  ) => void
  onClose: () => void
}

export const NewVersionDialog = ({
  open,
  templateUuid,
  onAfterSubmit,
  onClose,
}: NewVersionDialogProps) => {
  const { fetch, createVersion } = useVersionReportVersionRepository()
  const [value, setValue] = useState<string>('')
  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }, [])
  const [loading, setLoading] = useState<boolean>(false)
  const isSubmittingRef = useRef(false)

  const handleClose = useCallback(() => {
    setValue('')
    onClose()
  }, [onClose])

  const onSubmit = useCallback(async () => {
    if (!templateUuid || isSubmittingRef.current) return
    isSubmittingRef.current = true
    setLoading(true)
    try {
      const response = await createVersion(templateUuid, value)
      const version = await fetch(response.uuid)
      if (!version) {
        handleClose()
        return
      }
      onAfterSubmit(response.uuid, value, response.isInitial, version)
      handleClose()
    } finally {
      isSubmittingRef.current = false
      setLoading(false)
    }
  }, [createVersion, fetch, onAfterSubmit, handleClose, templateUuid, value])

  const onEnter = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSubmit()
      }
    },
    [onSubmit]
  )

  const disabled = useMemo(() => !value || loading, [value, loading])
  return (
    <>
      <StyledDialog open={open} onClose={handleClose}>
        <StyledDialogTitle>
          <Title>報告書の新規作成</Title>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Input
            label={'報告書の名称'}
            value={value}
            onChange={onChange}
            onKeyDown={onEnter}
            disabled={loading}
          />
        </StyledDialogContent>
        <StyledDialogActions>
          <Button onClick={onSubmit} disabled={disabled} colorPattern="skyBlue">
            報告書を作成する
          </Button>
          <Button
            onClick={handleClose}
            colorPattern="monotone"
            variant="outlined"
          >
            {intl.formatMessage({ id: 'dialog.cancel' })}
          </Button>
        </StyledDialogActions>
      </StyledDialog>
      <Loading isLoading={loading} />
    </>
  )
}

const Input = styled((props: TextFieldProps) => (
  <TextField variant="outlined" {...props} />
))({
  width: '360px',
  '& .MuiOutlinedInput-input': {
    height: '14px',
    padding: '20px 16px',
    color: colorPalette.monotone[9],
    caretColor: colorPalette.skyBlue[7],
    '&:focus': {
      backgroundColor: colorPalette.blue[0],
    },
    fontSize: '16px !important',
  },
  '& .MuiOutlinedInput-root': {
    height: '54px',
    '& fieldset': {
      borderColor: colorPalette.monotone[2],
    },
    '&:hover fieldset': {
      borderColor: colorPalette.skyBlue[7],
    },
    '&.Mui-focused fieldset': {
      borderColor: colorPalette.skyBlue[7],
      borderWidth: '1px',
    },
  },
})
